import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Formik } from "formik";
import { Form, Col, Row } from "react-bootstrap";

import SimpleModal from "components/SimpleModal";

import { createLoadingSelector } from "store/selectors";
import { createErrorSelector } from "store/selectors";
import { linkCompanyBank } from "actions/adminActions";
import { adminConstants } from "actions/types";
import { toast } from "react-toastify";
import type { RootState } from "types/state";
import type { BankAccountType } from "types/graphql";

import * as yup from "yup";

interface OwnProps {
  onClose: () => void;
  companyId?: string;
}

/**
 * confirmAccountNumber / confirmRoutingNumber are absent from initialValues
 * (start undefined) but read unconditionally in render, hence optional string.
 */
interface FormValues {
  accountNumber: string;
  routingNumber: string;
  bankName: string;
  accountType: string;
  confirmAccountNumber?: string;
  confirmRoutingNumber?: string;
}

const schema = yup.object({
  accountNumber: yup.string().required().matches(/^\d+$/),
  confirmAccountNumber: yup
    .string()
    .required()
    .label("Confirm account number")
    .test(
      "account-numbers-match",
      "Account numbers must match",
      function (value) {
        return this.parent.accountNumber === value;
      }
    ),
  routingNumber: yup.string().required().matches(/^\d+$/),
  confirmRoutingNumber: yup
    .string()
    .required()
    .label("Confirm routing number")
    .test(
      "routing-numbers-match",
      "Routing numbers must match",
      function (value) {
        return this.parent.routingNumber === value;
      }
    ),
  bankName: yup.string().required(),
  accountType: yup.string().required(),
});

const isFetchingSelector = createLoadingSelector(adminConstants.LINK_BANK);
const errorSelector = createErrorSelector(adminConstants.LINK_BANK);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isFetchingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  linkCompanyBank,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

class BankLinkModal extends React.PureComponent<Props> {
  _linkBank = ({
    accountNumber,
    routingNumber,
    accountType,
    bankName,
  }: FormValues) => {
    const bankRequest = {
      accountNumber,
      routingNumber,
      // FormValues.accountType is widened to string (matches initialValues +
      // yup.string()); the radios only ever emit "CHECKING"/"SAVINGS".
      accountType: accountType as BankAccountType,
      // companyId prop is optional but the mutation requires it; master passed
      // it unguarded, so assert non-null to preserve that behavior.
      companyId: this.props.companyId!,
      bankName,
    };

    void this.props
      .linkCompanyBank(this.props.client as ApolloClient<object>, bankRequest)
      .then((result) => {
        if (result.success) {
          // found-not-fixed: "Succesfully" is a pre-existing user-facing typo,
          // preserved verbatim; a content pass should correct it.
          toast.success("Succesfully linked bank.");
          this.props.onClose();
        }
      });
  };

  render() {
    return (
      <Formik<FormValues>
        validateOnChange={false}
        validationSchema={schema}
        enableReinitialize={true}
        onSubmit={(values) => {
          this._linkBank(values);
        }}
        initialValues={{
          accountNumber: "",
          routingNumber: "",
          bankName: "",
          accountType: "CHECKING",
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
        }) => (
          <SimpleModal
            show={true}
            onClose={this.props.onClose}
            // SimpleModal.onSubmit is typed (...args: unknown[]) => void and
            // calls it with no args; Formik's handleSubmit takes an optional
            // FormEvent, a contravariantly-disjoint param. Master passed
            // handleSubmit here verbatim, so boundary-cast to preserve it.
            onSubmit={handleSubmit as unknown as (...args: unknown[]) => void}
            title="Setup manual bank link"
            buttonText="Link"
            closeButtonText="Cancel"
            isXhrRequest={true}
            error={this.props.error}
            isFetching={this.props.isFetching}
          >
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="formBankName">
                <Form.Control
                  name="bankName"
                  placeholder="Bank Name"
                  value={values.bankName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.bankName && !!errors.bankName}
                  isValid={touched.bankName && !errors.bankName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bankName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="formAccountNumber">
                <Form.Control
                  name="accountNumber"
                  placeholder="Account Number"
                  value={values.accountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.accountNumber && !!errors.accountNumber}
                  isValid={touched.accountNumber && !errors.accountNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.accountNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formConfirmAccountNumber">
                <Form.Control
                  name="confirmAccountNumber"
                  placeholder="Confirm Account Number"
                  value={values.confirmAccountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    touched.confirmAccountNumber &&
                    !!errors.confirmAccountNumber
                  }
                  isValid={
                    touched.confirmAccountNumber && !errors.confirmAccountNumber
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmAccountNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formRoutingNumber">
                <Form.Control
                  name="routingNumber"
                  placeholder="Routing Number"
                  value={values.routingNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.routingNumber && !!errors.routingNumber}
                  isValid={touched.routingNumber && !errors.routingNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.routingNumber}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="formConfirmRoutingNumber">
                <Form.Control
                  name="confirmRoutingNumber"
                  placeholder="Confirm Routing Number"
                  value={values.confirmRoutingNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    touched.confirmRoutingNumber &&
                    !!errors.confirmRoutingNumber
                  }
                  isValid={
                    touched.confirmRoutingNumber && !errors.confirmRoutingNumber
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {/* found-not-fixed: renders confirmAccountNumber error under
                      the Confirm Routing Number field (copy-paste; would
                      otherwise be errors.confirmRoutingNumber) */}
                  {errors.confirmAccountNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formAccountType" as={Row}>
                <Col md={3}>
                  <Form.Check
                    type="radio"
                    name="accountType"
                    id="CHECKING"
                    label="Checking"
                    value="CHECKING"
                    onChange={handleChange}
                    checked={values.accountType === "CHECKING"}
                  />
                </Col>

                <Col md={3}>
                  <Form.Check
                    type="radio"
                    name="accountType"
                    label="Savings"
                    value="SAVINGS"
                    id="SAVINGS"
                    onChange={handleChange}
                    checked={values.accountType === "SAVINGS"}
                  />
                </Col>
              </Form.Group>
            </Form>
          </SimpleModal>
        )}
      </Formik>
    );
  }
}

export default connector(withApollo<Props>(BankLinkModal));
